/**
 * Authentication Redux Slice
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import secureStorage from '../../utils/secureStorage';

const initialState = {
  isAuthenticated: false,
  userSession: null,
  userId: null,
  clientId: null,
  token: null,
  wsSession: null,
  orderToken: null,
  loading: false,
  error: null,
};

// Async thunk for user login
export const login = createAsyncThunk(
  'auth/login',
  async ({ userId, authCode, appCode }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/login', { userId, authCode, appCode });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

// Async thunk for creating WS session
export const createWsSession = createAsyncThunk(
  'auth/createWsSession',
  async (userSession, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/ws-session', { userSession });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

// Async thunk for creating order WS token
export const createOrderWsToken = createAsyncThunk(
  'auth/createOrderWsToken',
  async (userSession, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/order-ws-token', { userSession });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false;
      state.userSession = null;
      state.userId = null;
      state.clientId = null;
      state.token = null;
      state.wsSession = null;
      state.orderToken = null;
      
      // Preserve watchlist and trading view settings before clearing storage
      const watchlist = localStorage.getItem('watchlist');
      const quickOrderCEStrike = localStorage.getItem('quickOrder_selectedCEStrike');
      const quickOrderPEStrike = localStorage.getItem('quickOrder_selectedPEStrike');
      const quickOrderType = localStorage.getItem('quickOrder_orderType');
      const quickOrderProductType = localStorage.getItem('quickOrder_productType');
      const quickOrderCEQty = localStorage.getItem('quickOrder_ceQty');
      const quickOrderPEQty = localStorage.getItem('quickOrder_peQty');
      const tradingViewNextExpiryDate = localStorage.getItem('tradingView_nextExpiryDate');
      const tradingViewNextExpirySymbols = localStorage.getItem('tradingView_nextExpirySymbols');
      const tradingViewSelectedChainInfo = localStorage.getItem('tradingView_selectedChainInfo');
      const tradingViewBaselineCESum = localStorage.getItem('tradingView_baselineCESum');
      const tradingViewBaselinePESum = localStorage.getItem('tradingView_baselinePESum');
      
      // Clear all storage
      secureStorage.clear();
      localStorage.clear();
      sessionStorage.clear();
      
      // Restore preserved items
      if (watchlist) localStorage.setItem('watchlist', watchlist);
      if (quickOrderCEStrike) localStorage.setItem('quickOrder_selectedCEStrike', quickOrderCEStrike);
      if (quickOrderPEStrike) localStorage.setItem('quickOrder_selectedPEStrike', quickOrderPEStrike);
      if (quickOrderType) localStorage.setItem('quickOrder_orderType', quickOrderType);
      if (quickOrderProductType) localStorage.setItem('quickOrder_productType', quickOrderProductType);
      if (quickOrderCEQty) localStorage.setItem('quickOrder_ceQty', quickOrderCEQty);
      if (quickOrderPEQty) localStorage.setItem('quickOrder_peQty', quickOrderPEQty);
      if (tradingViewNextExpiryDate) localStorage.setItem('tradingView_nextExpiryDate', tradingViewNextExpiryDate);
      if (tradingViewNextExpirySymbols) localStorage.setItem('tradingView_nextExpirySymbols', tradingViewNextExpirySymbols);
      if (tradingViewSelectedChainInfo) localStorage.setItem('tradingView_selectedChainInfo', tradingViewSelectedChainInfo);
      if (tradingViewBaselineCESum) localStorage.setItem('tradingView_baselineCESum', tradingViewBaselineCESum);
      if (tradingViewBaselinePESum) localStorage.setItem('tradingView_baselinePESum', tradingViewBaselinePESum);
    },
    setWsSession: (state, action) => {
      state.wsSession = action.payload;
    },
    setOrderToken: (state, action) => {
      state.orderToken = action.payload;
    },
    restoreSession: (state) => {
      // Restore from secure storage
      const authData = secureStorage.getItem('authData');
      
      if (authData && authData.token) {
        state.isAuthenticated = true;
        state.userSession = authData.userSession;
        state.userId = authData.userId;
        state.clientId = authData.clientId;
        state.token = authData.token;
      }
    },
  },
  extraReducers: (builder) => {
    // Login
    builder.addCase(login.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(login.fulfilled, (state, action) => {
      state.loading = false;
      state.isAuthenticated = true;
      state.userSession = action.payload.data.userSession;
      state.userId = action.payload.data.userId;
      state.clientId = action.payload.data.clientId;
      state.token = action.payload.data.token;
      
      // Store in secure storage
      secureStorage.setItem('authData', {
        userSession: action.payload.data.userSession,
        userId: action.payload.data.userId,
        clientId: action.payload.data.clientId,
        token: action.payload.data.token,
        timestamp: Date.now()
      });
    });
    builder.addCase(login.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.error || 'Login failed';
    });

    // Create WS Session
    builder.addCase(createWsSession.fulfilled, (state, action) => {
      if (action.payload?.data?.wsSess) {
        state.wsSession = action.payload.data.wsSess;
      }
    });
    builder.addCase(createWsSession.rejected, (state, action) => {
      console.error('WS Session creation failed:', action.payload);
    });

    // Create Order WS Token
    builder.addCase(createOrderWsToken.fulfilled, (state, action) => {
      if (action.payload?.data?.orderToken) {
        state.orderToken = action.payload.data.orderToken;
      }
    });
    builder.addCase(createOrderWsToken.rejected, (state, action) => {
      console.error('Order WS Token creation failed:', action.payload);
    });
  },
});

export const { logout, setWsSession, setOrderToken, restoreSession } = authSlice.actions;
export default authSlice.reducer;
